import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Shield, Lock, Eye, Database, Trash2, Mail } from 'lucide-react';

export default function PrivacyPolicy() {
    const lastUpdated = "February 28, 2025";
    
    return (
        <div className="min-h-screen bg-background py-8 lg:py-12" data-testid="privacy-policy-page">
            <div className="max-w-4xl mx-auto px-4">
                <Link to="/">
                    <Button variant="ghost" className="mb-6">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Home
                    </Button>
                </Link>

                <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Shield className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                        <h1 className="font-heading text-3xl font-bold">Privacy Policy</h1>
                        <p className="text-muted-foreground">Last updated: {lastUpdated}</p>
                    </div>
                </div>

                <Card className="mb-6">
                    <CardContent className="p-6 prose prose-slate dark:prose-invert max-w-none">
                        <p className="text-lg text-muted-foreground">
                            MediGuide AI ("we", "our", or "us") is committed to protecting your privacy. 
                            This Privacy Policy explains how we collect, use, disclose, and safeguard your 
                            information when you use our mobile application and website.
                        </p>
                    </CardContent>
                </Card>

                <div className="space-y-6">
                    {/* Information We Collect */}
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <Database className="w-5 h-5 text-primary" />
                                <h2 className="font-heading text-xl font-semibold">1. Information We Collect</h2>
                            </div>
                            
                            <h3 className="font-semibold mt-4 mb-2">Personal Information</h3>
                            <p className="text-muted-foreground mb-3">
                                When you create an account, we may collect:
                            </p>
                            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                                <li>Name and email address</li>
                                <li>Profile picture (optional)</li>
                                <li>Phone number (optional)</li>
                                <li>Date of birth (optional)</li>
                            </ul>

                            <h3 className="font-semibold mt-4 mb-2">Health Information</h3>
                            <p className="text-muted-foreground mb-3">
                                To provide symptom assessments, we collect:
                            </p>
                            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                                <li>Symptoms you report</li>
                                <li>Body parts affected</li>
                                <li>Duration and severity of symptoms</li>
                                <li>Medical history you choose to provide</li>
                                <li>Photos of visible symptoms (optional)</li>
                            </ul>

                            <h3 className="font-semibold mt-4 mb-2">Automatically Collected Information</h3>
                            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                                <li>Device type and operating system</li>
                                <li>Browser type</li>
                                <li>IP address</li>
                                <li>Usage patterns and preferences</li>
                            </ul>
                        </CardContent>
                    </Card>

                    {/* How We Use Your Information */}
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <Eye className="w-5 h-5 text-primary" />
                                <h2 className="font-heading text-xl font-semibold">2. How We Use Your Information</h2>
                            </div>
                            <p className="text-muted-foreground mb-3">We use the information we collect to:</p>
                            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                                <li>Provide AI-powered health assessments</li>
                                <li>Create and manage your account</li>
                                <li>Store your symptom check history</li>
                                <li>Improve our AI algorithms and services</li>
                                <li>Send important service notifications</li>
                                <li>Respond to your inquiries and support requests</li>
                                <li>Comply with legal obligations</li>
                            </ul>
                        </CardContent>
                    </Card>

                    {/* Data Security */}
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <Lock className="w-5 h-5 text-primary" />
                                <h2 className="font-heading text-xl font-semibold">3. Data Security</h2>
                            </div>
                            <p className="text-muted-foreground mb-3">
                                We implement robust security measures to protect your information:
                            </p>
                            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                                <li><strong>Encryption:</strong> All data is encrypted in transit using TLS/SSL</li>
                                <li><strong>Secure Storage:</strong> Data is stored in secure, encrypted databases</li>
                                <li><strong>Access Controls:</strong> Strict access controls limit who can view your data</li>
                                <li><strong>Regular Audits:</strong> We conduct regular security assessments</li>
                            </ul>
                        </CardContent>
                    </Card>

                    {/* Data Sharing */}
                    <Card>
                        <CardContent className="p-6">
                            <h2 className="font-heading text-xl font-semibold mb-4">4. Data Sharing</h2>
                            <p className="text-muted-foreground mb-3">
                                We do <strong>NOT</strong> sell your personal or health information. We may share data only:
                            </p>
                            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                                <li><strong>With your consent:</strong> When you choose to share results with healthcare providers</li>
                                <li><strong>Service providers:</strong> Third parties who help us operate our services (under strict confidentiality)</li>
                                <li><strong>Legal requirements:</strong> When required by law or to protect rights and safety</li>
                                <li><strong>Anonymized data:</strong> Aggregated, de-identified data for research and improvement</li>
                            </ul>
                        </CardContent>
                    </Card>

                    {/* Your Rights */}
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <Trash2 className="w-5 h-5 text-primary" />
                                <h2 className="font-heading text-xl font-semibold">5. Your Rights</h2>
                            </div>
                            <p className="text-muted-foreground mb-3">You have the right to:</p>
                            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                                <li><strong>Access:</strong> Request a copy of your personal data</li>
                                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                                <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                                <li><strong>Portability:</strong> Export your health assessment history</li>
                                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                                <li><strong>Withdraw consent:</strong> Revoke previously given consent</li>
                            </ul>
                            <p className="text-muted-foreground mt-4">
                                To exercise these rights, visit Settings → Account → Deactivate Account, or contact us at privacy@mediguide.online
                            </p>
                        </CardContent>
                    </Card>

                    {/* Cookies */}
                    <Card>
                        <CardContent className="p-6">
                            <h2 className="font-heading text-xl font-semibold mb-4">6. Cookies and Tracking</h2>
                            <p className="text-muted-foreground mb-3">We use cookies and similar technologies to:</p>
                            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                                <li>Remember your preferences and settings</li>
                                <li>Keep you signed in</li>
                                <li>Understand how you use our app</li>
                                <li>Improve our services</li>
                            </ul>
                            <p className="text-muted-foreground mt-3">
                                You can control cookies through your browser settings.
                            </p>
                        </CardContent>
                    </Card>

                    {/* Children's Privacy */}
                    <Card>
                        <CardContent className="p-6">
                            <h2 className="font-heading text-xl font-semibold mb-4">7. Children's Privacy</h2>
                            <p className="text-muted-foreground">
                                MediGuide AI is not intended for children under 13. We do not knowingly collect 
                                information from children under 13. If you believe we have collected information 
                                from a child under 13, please contact us immediately.
                            </p>
                        </CardContent>
                    </Card>

                    {/* International Users */}
                    <Card>
                        <CardContent className="p-6">
                            <h2 className="font-heading text-xl font-semibold mb-4">8. International Users</h2>
                            <p className="text-muted-foreground mb-3">
                                <strong>GDPR (European Users):</strong> If you are in the European Economic Area, 
                                you have additional rights including the right to lodge a complaint with a supervisory authority.
                            </p>
                            <p className="text-muted-foreground">
                                <strong>CCPA (California Users):</strong> California residents have the right to know 
                                what personal information is collected and to request deletion.
                            </p>
                        </CardContent>
                    </Card>

                    {/* Changes */}
                    <Card>
                        <CardContent className="p-6">
                            <h2 className="font-heading text-xl font-semibold mb-4">9. Changes to This Policy</h2>
                            <p className="text-muted-foreground">
                                We may update this Privacy Policy from time to time. We will notify you of any 
                                changes by posting the new Privacy Policy on this page and updating the "Last updated" 
                                date. You are advised to review this Privacy Policy periodically.
                            </p>
                        </CardContent>
                    </Card>

                    {/* Contact */}
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <Mail className="w-5 h-5 text-primary" />
                                <h2 className="font-heading text-xl font-semibold">10. Contact Us</h2>
                            </div>
                            <p className="text-muted-foreground mb-4">
                                If you have any questions about this Privacy Policy, please contact us:
                            </p>
                            <div className="bg-muted/50 rounded-lg p-4">
                                <p className="font-medium">MediGuide AI</p>
                                <p className="text-muted-foreground">Email: privacy@mediguide.online</p>
                                <p className="text-muted-foreground">Website: https://mediguide.online</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="mt-8 text-center text-sm text-muted-foreground">
                    <p>© {new Date().getFullYear()} MediGuide AI. All rights reserved.</p>
                </div>
            </div>
        </div>
    );
}
